import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import Button from 'src/components/core/button';
import TextButton from 'src/components/core/button/text-button';
import { IrlEventTrackingEvent } from 'src/gql/graphql';
import type { IrlEvent } from 'src/gql/graphql';
import { useLoyaltyEnrollmentAnalytics } from 'src/hooks/use-loyalty-enrollment-analytics';
import { Integrations } from 'src/components/loyalty/use-loyalty/types';
import { Card } from './card';
import { IrlEventLogo } from './irl-event-logo';
import { ContentHeader, Heading, Description, CtaButtonStyles } from './styles';
import { useCreateEnterpriseDposCustomer } from '../hooks/use-create-enterprise-dpos-customer';
import { useIrlEventTracking } from '../hooks/use-irl-event-tracking';

type IrlEventLoyaltyEnrollProps = {
  irlEvent: IrlEvent;
  onEnroll: () => void;
  onSkip: () => void;
};

export function IrlEventLoyaltyEnroll({ irlEvent, onEnroll, onSkip }: IrlEventLoyaltyEnrollProps): JSX.Element {
  const [showDetails, setShowDetails] = useState(false);
  const [isOptedIn, setIsOptedIn] = useState(false);
  const { trackEvent } = useIrlEventTracking();
  const {
    createEnterpriseDposCustomer,
    loading,
    userFirstName,
    rewardsProgramName,
    enterpriseName,
  } = useCreateEnterpriseDposCustomer();
  const { trackViewed, trackSubmitted, trackSucceeded, trackFailed } = useLoyaltyEnrollmentAnalytics({
    surface: 'irlEvent',
    integration: Integrations.dutchiepos,
  });

  const hasTrackedViewedRef = useRef(false);
  useEffect(() => {
    if (hasTrackedViewedRef.current) {
      return;
    }
    hasTrackedViewedRef.current = true;
    trackViewed();
  }, [trackViewed]);

  const handleToggleDetails = (): void => {
    setShowDetails((prev) => !prev);
  };

  const handleSkipEnrollmentClick = (): void => {
    void trackEvent(IrlEventTrackingEvent.skippedLoyalty);
    onSkip();
  };

  const handleEnrollmentClick = async (): Promise<void> => {
    trackSubmitted();
    const success = await createEnterpriseDposCustomer({ isEnrollingLoyalty: true });
    if (!success) {
      trackFailed();
      return;
    }

    if (success.posCustomerId) {
      trackSucceeded();
    } else {
      trackFailed();
    }

    void trackEvent(IrlEventTrackingEvent.enrolledLoyalty);
    onEnroll();
  };

  const isEnrollmentDisabled = !isOptedIn || loading;
  const title = userFirstName ? `Welcome, ${userFirstName}` : 'Welcome';

  return (
    <>
      <MetaTags title='Loyalty Enrollment' description='Enroll in loyalty program' />

      <Card data-testid='irl-event-loyalty-enroll'>
        <IrlEventLogo headerImageUrl={irlEvent.headerImageUrl ?? null} />

        <ContentHeader>
          <Heading>{title}</Heading>
          <Description data-testid='irl-event-loyalty-enroll-description'>
            Connect your account to {rewardsProgramName}. Apply the rewards you've earned to your next order.
          </Description>
        </ContentHeader>

        <OptInContainer role='group' aria-label='Loyalty program opt-in'>
          <OptInRow>
            <Checkbox
              type='checkbox'
              checked={isOptedIn}
              onChange={(e) => setIsOptedIn(e.target.checked)}
              id='loyalty-opt-in'
              data-testid='irl-event-loyalty-enroll-agree-checkbox'
            />
            <OptInLabel htmlFor='loyalty-opt-in'>
              I agree to join {rewardsProgramName} and receive marketing communications to qualify for rewards.{' '}
              <DetailsLink
                onClick={handleToggleDetails}
                aria-expanded={showDetails}
                aria-controls='legal-details'
                data-testid='irl-event-loyalty-enroll-details-toggle'
              >
                {showDetails ? 'Hide details' : 'View details'}
              </DetailsLink>
            </OptInLabel>
          </OptInRow>

          {showDetails && (
            <LegalTextContainer id='legal-details'>
              <LegalText data-testid='irl-event-loyalty-enroll-legal-text'>
                I consent to sign up for the {rewardsProgramName} program where I will receive marketing communications.
                By doing so, I understand that I am allowing {enterpriseName} and its technology provider(s) to retain
                my personal contact details/engagement history for use in personalized marketing. I understand that the
                data collected by {enterpriseName} for the purposes of administering this loyalty program will not be
                transmitted to or stored by any state governments.
              </LegalText>
            </LegalTextContainer>
          )}
        </OptInContainer>

        <EnrollButton
          variant='primary'
          size='large'
          disabled={isEnrollmentDisabled}
          onClick={handleEnrollmentClick}
          loading={loading}
          aria-busy={loading}
          data-testid='irl-event-loyalty-enroll-agree-button'
        >
          Agree & Connect
        </EnrollButton>

        <SkipButton
          disabled={loading}
          onClick={handleSkipEnrollmentClick}
          data-testid='irl-event-loyalty-enroll-skip-button'
        >
          Not right now
        </SkipButton>
      </Card>
    </>
  );
}

const OptInContainer = styled.div`
  background-color: #f5f6f7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  padding: 16px;
`;

const OptInRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const Checkbox = styled.input`
  accent-color: #3387e1;
  cursor: pointer;
  flex-shrink: 0;
  height: 16px;
  margin: 0;
  width: 16px;
`;

const OptInLabel = styled.label`
  color: #485055;
  cursor: pointer;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
`;

const DetailsLink = styled.button`
  background: none;
  border: none;
  color: #485055;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const LegalTextContainer = styled.div`
  border-top: 1px solid #e3e7e9;
  padding-top: 12px;
`;

const LegalText = styled.p`
  color: #485055;
  font-size: 11px;
  line-height: 18px;
  margin: 0;
  text-align: left;
`;

const EnrollButton = styled(Button)`
  ${CtaButtonStyles}
  margin: 24px 0;

  &:disabled {
    background-color: #e3e7e9;
    color: #8e979c;
    cursor: not-allowed;
  }
`;

const SkipButton = styled(TextButton)`
  color: #485055;
  font-size: 14px;
  font-weight: 600;
  margin: 0 auto;
`;
